import React, { createContext, useContext, useState, useCallback } from 'react';
import datateamReprocess, { DatateamReprocessResponse } from '../services/datateamReprocess';

interface IDatateamReprocessContext {
  demandIds: string[];
  setDemandIds: (data: string[]) => void;
  environment: string;
  setEnvironment: (data: string) => void;
  idArtifact: number | null;
  setIdArtifact: (data: number | null) => void;
  results: DatateamReprocessResponse | null;
  setResults: (data: DatateamReprocessResponse | null) => void;
  reprocessDatateam: (demandIds: string[], environment: string, idArtifact?: number) => Promise<DatateamReprocessResponse>;
  loading: boolean;
  error: string | null;
}

export const DatateamReprocessContext = createContext<IDatateamReprocessContext>({
  demandIds: [],
  setDemandIds: () => {},
  environment: '',
  setEnvironment: () => {},
  idArtifact: null,
  setIdArtifact: () => {},
  results: null,
  setResults: () => {},
  reprocessDatateam: async () => ({ success: false, data: [] }),
  loading: false,
  error: null,
});

const DatateamReprocessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [demandIds, setDemandIdsState] = useState<string[]>([]);
  const [environment, setEnvironmentState] = useState<string>('');
  const [idArtifact, setIdArtifactState] = useState<number | null>(null);
  const [results, setResults] = useState<DatateamReprocessResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Wrapper para setDemandIds que limpa os resultados anteriores quando novos IDs são definidos
  const setDemandIds = useCallback((ids: string[]) => {
    // Limpa resultados anteriores quando novos IDs são definidos
    setResults(null);
    setError(null);
    setDemandIdsState(ids);
  }, []);

  const setEnvironment = useCallback((env: string) => {
    setResults(null);
    setError(null);
    setEnvironmentState(env);
  }, []);

  const setIdArtifact = useCallback((id: number | null) => {
    setResults(null);
    setError(null);
    setIdArtifactState(id);
  }, []);

  const reprocessDatateam = useCallback(async (
    ids: string[], 
    env: string, 
    idArt?: number
  ): Promise<DatateamReprocessResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await datateamReprocess.reprocess({ 
        demandIds: ids, 
        environment: env, 
        idArtifact: idArt ?? 0 // idArtifact sempre é 0 no supreq
      });
      setResults(response);
      return response;
    } catch (err: any) {
      console.error('Erro ao reprocessar DataTeam:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao reprocessar DataTeam';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <DatateamReprocessContext.Provider
      value={{
        demandIds,
        setDemandIds,
        environment,
        setEnvironment,
        idArtifact,
        setIdArtifact,
        results,
        setResults,
        reprocessDatateam,
        loading,
        error,
      }}
    >
      {children}
    </DatateamReprocessContext.Provider>
  );
};

const useDatateamReprocess = (): IDatateamReprocessContext => {
  const context = useContext(DatateamReprocessContext);
  if (!context) {
    throw new Error('useDatateamReprocess must be used within a DatateamReprocessProvider');
  }
  return context;
};

export { DatateamReprocessProvider, useDatateamReprocess };

